/**
 * Dependency-free community detection (synchronous label propagation) plus
 * convex-hull helpers for rendering soft cluster outlines.
 */

export const detectCommunities = (
  nodeIds: string[],
  adjacency: Map<string, Set<string>>,
): Map<string, number> => {
  const labels = new Map<string, number>(
    nodeIds.map((id, index) => [id, index]),
  );
  for (let iteration = 0; iteration < 12; iteration++) {
    let changed = false;
    for (const id of nodeIds) {
      const counts = new Map<number, number>();
      for (const neighbor of adjacency.get(id) ?? []) {
        const label = labels.get(neighbor);
        if (label === undefined) continue;
        counts.set(label, (counts.get(label) ?? 0) + 1);
      }
      if (counts.size === 0) continue;
      let best = labels.get(id)!;
      let bestCount = 0;
      for (const [label, count] of counts) {
        if (count > bestCount || (count === bestCount && label < best)) {
          best = label;
          bestCount = count;
        }
      }
      if (best !== labels.get(id)) {
        labels.set(id, best);
        changed = true;
      }
    }
    if (!changed) break;
  }

  const sizes = new Map<number, number>();
  labels.forEach((label) => sizes.set(label, (sizes.get(label) ?? 0) + 1));
  const ordered = [...sizes.entries()].sort((a, b) => b[1] - a[1]);
  const renumbered = new Map(ordered.map(([label], index) => [label, index]));
  const result = new Map<string, number>();
  labels.forEach((label, id) => result.set(id, renumbered.get(label)!));
  return result;
};

type Point = [number, number];

const cross = (o: Point, a: Point, b: Point): number =>
  (a[0] - o[0]) * (b[1] - o[1]) - (a[1] - o[1]) * (b[0] - o[0]);

export const convexHull = (points: Point[]): Point[] => {
  if (points.length <= 2) return points;
  const sorted = [...points].sort((a, b) => a[0] - b[0] || a[1] - b[1]);
  const lower: Point[] = [];
  for (const p of sorted) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    ) {
      lower.pop();
    }
    lower.push(p);
  }
  const upper: Point[] = [];
  for (const p of [...sorted].reverse()) {
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    ) {
      upper.pop();
    }
    upper.push(p);
  }
  return [...lower.slice(0, -1), ...upper.slice(0, -1)];
};

// Rendered with a fat round-joined stroke of the same color, this polygon
// reads as a soft padded hull without any offset-polygon math.
export const hullPath = (points: Point[]): string => {
  if (points.length === 0) return '';
  if (points.length === 1) {
    const [x, y] = points[0];
    return `M ${x} ${y} L ${x + 0.01} ${y}`;
  }
  const hull = convexHull(points);
  return (
    hull
      .map(([x, y], index) => `${index === 0 ? 'M' : 'L'} ${x} ${y}`)
      .join(' ') + ' Z'
  );
};
